/**
 * 教育类知识点讲解效果
 * 
 * 基于足球视频质量标准（9.0/10）制作
 * 
 * 核心功能：
 * 1. 标题动画（Spring 平滑入场）
 * 2. 要点逐条显示（延迟入场）
 * 3. 强调框（关键点突出）
 * 4. 数据驱动（可配置内容）
 * 
 * 技术亮点：
 * - Spring 物理动画
 * - interpolate 插值计算
 * - 数据驱动可视化
 * - 模块化设计
 * - 详细注释
 * 
 * 适用场景：
 * - 在线课程视频
 * - 知识点讲解
 * - 教育培训视频
 * - PPT 动画视频化
 * 
 * @author 龙虾菌 🦞
 * @date 2026-03-10
 */

import React from 'react';
import {
  AbsoluteFill,
  useCurrentFrame,
  useVideoConfig,
  interpolate,
  spring,
} from 'remotion';

// ==================== 类型定义 ====================

/**
 * 知识点数据结构
 */
interface KnowledgePoint {
  title: string;
  subtitle: string;
  points: Point[];
  emphasis?: string;
}

/**
 * 要点数据结构
 */
interface Point {
  icon: string;
  text: string;
  subtext?: string;
}

// ==================== 配置数据 ====================

/**
 * 知识点配置（可自定义修改）
 */
const LESSON_DATA: KnowledgePoint = {
  title: 'React Hooks 核心概念',
  subtitle: '现代 React 开发必备知识',
  points: [
    {
      icon: '🎯',
      text: 'useState - 状态管理',
      subtext: '在函数组件中添加本地状态',
    },
    {
      icon: '⚡',
      text: 'useEffect - 副作用处理',
      subtext: '处理数据获取、订阅、DOM 操作',
    },
    {
      icon: '🔄',
      text: 'useContext - 上下文共享',
      subtext: '跨组件层级传递数据',
    },
    {
      icon: '🚀',
      text: 'useCallback - 性能优化',
      subtext: '缓存回调函数避免重复渲染',
    },
  ],
  emphasis: '💡 提示：掌握这 4 个 Hooks 即可应对 80% 的开发场景',
};

// ==================== 子组件 ====================

/**
 * 标题组件
 */
const Title: React.FC<{ title: string; subtitle: string; frame: number; fps: number }> = ({
  title,
  subtitle,
  frame,
  fps,
}) => {
  // 标题 Spring 动画
  const titleSpring = spring({
    frame,
    fps,
    config: {
      damping: 15,
      stiffness: 100,
      mass: 0.5,
    },
  });

  // 副标题延迟入场
  const subtitleSpring = spring({
    frame: frame - 10,
    fps,
    config: {
      damping: 20,
      stiffness: 80,
    },
  });

  return (
    <div
      style={{
        position: 'absolute',
        top: 80,
        left: 80,
        right: 80,
      }}
    >
      {/* 主标题 */}
      <h1
        style={{
          fontSize: 72,
          fontWeight: 'bold',
          color: '#fff',
          margin: 0,
          textShadow: '0 0 40px rgba(59, 130, 246, 0.6)',
          transform: `translateY(${interpolate(titleSpring, [0, 1], [50, 0])}px)`,
          opacity: titleSpring,
        }}
      >
        {title}
      </h1>

      {/* 副标题 */}
      <p
        style={{
          fontSize: 32,
          color: 'rgba(255, 255, 255, 0.8)',
          margin: '15px 0 0 0', // 减小 margin: 20px → 15px
          transform: `translateY(${interpolate(subtitleSpring, [0, 1], [30, 0])}px)`,
          opacity: subtitleSpring,
        }}
      >
        {subtitle}
      </p>

      {/* 装饰线 */}
      <div
        style={{
          width: interpolate(titleSpring, [0, 1], [0, 300]),
          height: 4,
          background: 'linear-gradient(90deg, #3b82f6 0%, #8b5cf6 100%)',
          marginTop: 15, // 副标题与装饰线的间距
          marginBottom: 60, // 装饰线与下方内容的间距（taste-skill: 大量留白）
          borderRadius: 2,
        }}
      />
    </div>
  );
};

/**
 * 要点项组件
 */
const PointItem: React.FC<{
  point: Point;
  index: number;
  frame: number;
  fps: number;
}> = ({ point, index, frame, fps }) => {
  const delay = 30 + index * 15;

  // 图标 Spring 动画
  const iconSpring = spring({
    frame: frame - delay,
    fps,
    config: {
      damping: 12,
      stiffness: 100,
      mass: 0.5,
    },
  });

  // 文字渐入动画
  const textProgress = interpolate(frame, [delay + 10, delay + 40], [0, 1], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  });

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 20,
        marginBottom: 30,
        transform: `translateX(${interpolate(iconSpring, [0, 1], [50, 0])}px)`,
        opacity: iconSpring,
      }}
    >
      {/* 图标 */}
      <div
        style={{
          fontSize: 48,
          width: 80,
          height: 80,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'rgba(59, 130, 246, 0.2)',
          borderRadius: 16,
          border: '2px solid rgba(59, 130, 246, 0.4)',
        }}
      >
        {point.icon}
      </div>

      {/* 文字内容 */}
      <div style={{ flex: 1 }}>
        <div
          style={{
            fontSize: 36,
            fontWeight: 'bold',
            color: '#fff',
            marginBottom: 8,
            opacity: textProgress,
          }}
        >
          {point.text}
        </div>
        {point.subtext && (
          <div
            style={{
              fontSize: 24,
              color: 'rgba(255, 255, 255, 0.7)',
              opacity: interpolate(frame, [delay + 20, delay + 50], [0, 1], {
                extrapolateLeft: 'clamp',
                extrapolateRight: 'clamp',
              }),
            }}
          >
            {point.subtext}
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * 强调框组件
 */
const EmphasisBox: React.FC<{
  text: string;
  frame: number;
  fps: number;
}> = ({ text, frame, fps }) => {
  const delay = 120;

  // 强调框 Spring 动画
  const emphasisSpring = spring({
    frame: frame - delay,
    fps,
    config: {
      damping: 15,
      stiffness: 80,
      mass: 0.5,
    },
  });

  return (
    <div
      style={{
        position: 'absolute',
        bottom: 60,
        left: 80,
        right: 80,
        background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.3) 0%, rgba(236, 72, 153, 0.3) 100%)',
        padding: '30px 40px',
        borderRadius: 20,
        border: '2px solid rgba(255, 255, 255, 0.2)',
        backdropFilter: 'blur(10px)',
        transform: `translateY(${interpolate(emphasisSpring, [0, 1], [30, 0])}px)`,
        opacity: emphasisSpring,
        boxShadow: '0 10px 40px rgba(139, 92, 246, 0.4)',
      }}
    >
      <div
        style={{
          fontSize: 28,
          color: '#fff',
          lineHeight: 1.6,
        }}
      >
        {text}
      </div>
    </div>
  );
};

// ==================== 主组件 ====================

/**
 * 教育类知识点讲解效果
 * 
 * 参数：
 * - data: KnowledgePoint - 知识点数据（可选，默认使用 LESSON_DATA）
 */
export const EducationalSlides: React.FC<{ data?: KnowledgePoint }> = ({
  data = LESSON_DATA,
}) => {
  const frame = useCurrentFrame();
  const { width, height, fps } = useVideoConfig();

  return (
    <AbsoluteFill
      style={{
        background: 'linear-gradient(135deg, #1e3a8a 0%, #312e81 50%, #4c1d95 100%)',
        fontFamily: 'Arial, sans-serif',
      }}
    >
      {/* 标题区域 */}
      <Title
        title={data.title}
        subtitle={data.subtitle}
        frame={frame}
        fps={fps}
      />

      {/* 要点列表 */}
      <div
        style={{
          position: 'absolute',
          top: 300, // 遵循 taste-skill 规范增加与副标题装饰线的间距
          left: 80,
          right: 80,
        }}
      >
        {data.points.map((point, index) => (
          <PointItem
            key={index}
            point={point}
            index={index}
            frame={frame}
            fps={fps}
          />
        ))}
      </div>

      {/* 强调框 */}
      {data.emphasis && (
        <EmphasisBox text={data.emphasis} frame={frame} fps={fps} />
      )}

      {/* 进度指示器（右上角） */}
      <div
        style={{
          position: 'absolute',
          top: 40,
          right: 40,
          background: 'rgba(0, 0, 0, 0.4)',
          padding: '15px 25px',
          borderRadius: 12,
          border: '1px solid rgba(255, 255, 255, 0.2)',
        }}
      >
        <div style={{ fontSize: 14, color: 'rgba(255, 255, 255, 0.7)' }}>
          知识点进度
        </div>
        <div style={{ fontSize: 24, color: '#fff', fontWeight: 'bold' }}>
          {Math.round(
            interpolate(frame, [0, 150], [0, 100], {
              extrapolateLeft: 'clamp',
              extrapolateRight: 'clamp',
            })
          )}
          %
        </div>
      </div>

      {/* 装饰元素（光点） */}
      {[0, 1, 2].map((i) => (
        <div
          key={i}
          style={{
            position: 'absolute',
            top: 40 + i * 25,
            right: 200,
            width: 12,
            height: 12,
            background: i === 0 ? '#3b82f6' : i === 1 ? '#8b5cf6' : '#ec4899',
            borderRadius: '50%',
            boxShadow: `0 0 20px ${i === 0 ? '#3b82f6' : i === 1 ? '#8b5cf6' : '#ec4899'}`,
            opacity: interpolate(frame, [i * 10, i * 10 + 20, i * 10 + 25, i * 10 + 30], [0, 0, 1, 1], {
              extrapolateLeft: 'clamp',
              extrapolateRight: 'clamp',
            }),
          }}
        />
      ))}
    </AbsoluteFill>
  );
};

// ==================== 默认配置 ====================

/**
 * 默认导出配置
 */
export default {
  id: 'EducationalSlides',
  component: EducationalSlides,
  durationInFrames: 150, // 5 秒 @ 30 FPS
  fps: 30,
  width: 1920,
  height: 1080,
};
